{
  "title": "Settings",
  "subtitle": "How the company is set up: its identity, its printed documents and the rules its books follow.",
  "search": {
    "label": "Search settings",
    "placeholder": "Search settings…",
    "clear": "Clear search",
    "empty": "No setting matches “{{query}}”. Try another word, such as VAT, logo or bank.",
    "found_one": "{{n}} match",
    "found_other": "{{n}} matches"
  },
  "rail": {
    "label": "Settings sections",
    "unsaved": "Unsaved changes",
    "opensPage": "(opens its page)",
    "back": "Back to all settings"
  },
  "groups": {
    "personal": "You",
    "company": "Company",
    "documents": "Documents",
    "salesPurchasing": "Sales & purchasing",
    "inventory": "Inventory",
    "accounting": "Accounting",
    "work": "Work",
    "communication": "Communication",
    "automation": "Automation & workflows",
    "administration": "Administration"
  },
  "sections": {
    "appearance": {
      "title": "Appearance",
      "intro": "How Sahab looks for you on this browser. Changes apply as you pick them."
    },
    "company": {
      "title": "Company profile",
      "intro": "The name shown in the app and the legal identity printed at the head of every document."
    },
    "typography": {
      "title": "Fonts and text",
      "intro": "The typeface and text size of every screen, for everyone in the company. Printed documents keep their own."
    },
    "printing": {
      "title": "Printing",
      "intro": "What invoices and other documents carry at their foot: footer, terms, how to pay you, and sharing."
    },
    "numbering": {
      "title": "Numbering",
      "intro": "How each kind of document is numbered, and the number the next one gets."
    },
    "defaults": {
      "title": "Defaults",
      "intro": "What new sales and purchase documents start with. Documents already saved keep their own."
    },
    "payment-methods": {
      "title": "Payment methods",
      "intro": "The ways your company is paid and pays (Visa, Thawani, bank transfers) and the cash or bank account each starts on in receipts, refunds and supplier payments."
    },
    "tax-rates": {
      "title": "Tax rates",
      "intro": "VAT and other rates offered on documents; the default is used for new lines."
    },
    "stock-policy": {
      "title": "Stock policy",
      "intro": "What happens when a sale takes stock below zero, at invoice posting and at the till."
    },
    "account-mapping": {
      "title": "Account mapping",
      "intro": "Which account each kind of posting uses. Documents post to these accounts automatically."
    },
    "lock-date": {
      "title": "Lock date",
      "intro": "Closes the books up to a date: nothing dated on or before it can be posted or changed."
    },
    "accounting-checks": {
      "title": "Accounting checks",
      "intro": "Which Books health checks run, and when each one speaks up."
    },
    "tasks": {
      "title": "Tasks",
      "intro": "How tasks are reviewed and rescheduled, and when team leads hear about late work."
    },
    "service": {
      "title": "Service jobs",
      "intro": "When technicians may record work, and what a visit report needs before a visit can finish."
    },
    "service-site-templates": {
      "title": "Site templates",
      "intro": "How the places of a customer's site are named level by level: Floor › Apartment, Level › Shop. Each site picks one."
    },
    "service-asset-types": {
      "title": "Asset types",
      "intro": "The kinds of assets the company services, each with its own details, checklist, tag prefix and usual service interval."
    },
    "contracts": {
      "title": "Maintenance contracts",
      "intro": "When renewals are reminded, how early planned visits become jobs, and what a new contract starts with."
    },
    "projects": {
      "title": "Projects",
      "intro": "The functions projects can use, the type a new project starts from and how progress is measured without one."
    },
    "project-types": {
      "title": "Project types",
      "intro": "The kinds of project the company runs, each with its functions, billing method and a plan template dated in working days."
    },
    "calendar": {
      "title": "Working calendar",
      "intro": "The working week and the company's holidays. Due dates and approval timers counted in working days skip the days off."
    },
    "organization": {
      "title": "Organisation",
      "intro": "The organisation's units, each under the unit it belongs to, with their heads, acting heads and HR business partners. Approvals and who sees which records follow this tree."
    },
    "work-locations": {
      "title": "Work locations",
      "intro": "The offices and sites where people work, by governorate and wilayat. Units name one as their default."
    },
    "teams": {
      "title": "Teams",
      "intro": "People grouped with their leads. Teams own tasks and make service visits, whatever modules the company uses."
    },
    "privacy": {
      "title": "Privacy",
      "intro": "How long personal data is kept, within the legal minimums, and the processing register."
    },
    "legal-holds": {
      "title": "Legal holds",
      "intro": "Records kept out of retention while a dispute, audit or claim is open."
    },
    "notifications": {
      "title": "Messaging",
      "intro": "Who customer e-mails come from, where replies go and which mail service sends them."
    },
    "ai": {
      "title": "AI assistant",
      "intro": "The assistant under your company's own OpenAI key: whether it is on, the key, the model, limits, features and how long conversations are kept."
    },
    "modules": {
      "title": "Modules",
      "intro": "The parts of Sahab ERP your company uses."
    },
    "regional": {
      "title": "Regional",
      "intro": "How dates, numbers and money are written everywhere in the app and on documents."
    },
    "system": {
      "title": "System information",
      "intro": "Which version of the app this is, and where to get help."
    },
    "project-cost-categories": {
      "title": "Cost categories",
      "intro": "How project costs are grouped and budgeted, the account each posts to, and which category a cost gets when none is chosen."
    },
    "project-insights": {
      "title": "Project insights",
      "intro": "When a project's health turns amber or red: the threshold of each signal in plain words, and how the expected final cost is worked out."
    },
    "project-contracting": {
      "title": "Contracting",
      "intro": "The terms a new contract starts from: how work is measured, when certificates are paid, retention, the advance and delay damages."
    },
    "project-rate-roles": {
      "title": "Rate roles",
      "intro": "The roles people bill time at, such as Site engineer or Consultant, each with its bill rate per hour."
    },
    "commissions": {
      "title": "Commission",
      "intro": "Who approves a commission run, and whether approved commission is paid on the payslip."
    }
  },
  "links": {
    "tills": {
      "label": "Tills",
      "status_one": "{{n}} active till",
      "status_other": "{{n}} active tills"
    },
    "warehouses": {
      "label": "Warehouses",
      "status_one": "{{n}} warehouse",
      "status_other": "{{n}} warehouses"
    },
    "fiscalYears": {
      "label": "Fiscal years",
      "open": "{{name}} open",
      "closed": "{{name}} closed",
      "none": "No year yet"
    },
    "cashBank": {
      "label": "Cash & bank",
      "status_one": "{{n}} account",
      "status_other": "{{n}} accounts"
    },
    "users": {
      "label": "Users",
      "status_one": "{{n}} active user",
      "status_other": "{{n}} active users"
    },
    "roles": {
      "label": "Roles",
      "status_one": "{{n}} role",
      "status_other": "{{n}} roles"
    },
    "auditLog": {
      "label": "Audit log",
      "status": "Who changed what, and when"
    },
    "workflows": {
      "label": "Approval workflows",
      "status": "Which purchases need approval, and by whom"
    },
    "workflowRuns": {
      "label": "Approval requests",
      "status": "Requests in progress, blocked or failed"
    },
    "delegations": {
      "label": "Delegations",
      "status": "Who approves for whom while away"
    },
    "messageTemplates": {
      "label": "Message templates",
      "status": "The wording of the e-mails customers get"
    },
    "messageOutbox": {
      "label": "Message outbox",
      "status": "What was sent, what failed and why"
    },
    "loyalty": {
      "label": "Loyalty programme",
      "status": "Earning, redeeming, tiers and bonus rules"
    },
    "payComponents": {
      "label": "Pay components",
      "status": "The salary structure: basic, allowances and deductions"
    },
    "contributionRules": {
      "label": "Contribution rules",
      "status": "Social Protection Fund rates by branch"
    },
    "payrollSettings": {
      "label": "Payroll settings",
      "status": "WPS, overtime, sick leave and gratuity"
    }
  },
  "documentTypes": {
    "invoice": "Invoice",
    "quotation": "Quotation",
    "credit_note": "Credit note",
    "receipt": "Receipt",
    "purchase": "Purchase order"
  },
  "frame": {
    "lastChanged": "Last changed {{date}}",
    "lastChangedBy": "Last changed {{date}} by {{name}}",
    "lastChangedByYou": "Last changed {{date}} by you",
    "history": "History",
    "needs": "Read only: changing it needs “{{permission}}”.",
    "fixed": {
      "provider": "Read only: changed by your provider.",
      "numbering": "Read only: the formats are fixed in this version; each series continues from its last number.",
      "regional": "Read only: fixed for companies in Oman.",
      "system": "Read only: set when the app is installed."
    }
  },
  "saveBar": {
    "unsaved": "Unsaved changes",
    "clean": "No unsaved changes",
    "discard": "Discard",
    "shortcut": "{{keys}} to save"
  },
  "conflict": {
    "title": "Someone else saved this section while you were editing",
    "body": "Your changes aren't saved yet. Load their changes, then re-apply yours.",
    "reload": "Reload their changes",
    "reloadedTitle": "Their changes are loaded",
    "youChanged": "You had changed: {{fields}}.",
    "separator": ", ",
    "reapply": "Re-apply my changes",
    "dismiss": "Dismiss"
  },
  "units": {
    "days": "days"
  },
  "proof": {
    "title": "Print proof",
    "unsaved": "Unsaved changes",
    "saved": "As saved",
    "show": "Preview",
    "hide": "Hide preview",
    "jump": "Preview",
    "language": "Print language",
    "a4": "Show as printed (A4)",
    "documentType": "Document",
    "note": "A sample document, printed with these settings before they are saved.",
    "receiptNote": "A receipt voucher prints the receipt terms and leaves out the footer and payment details. A refund's payment voucher prints no terms."
  },
  "company": {
    "blocks": {
      "name": "Name in the app",
      "nameBody": "Shown in the app and on documents that have no legal name yet.",
      "legal": "Legal identity",
      "legalBody": "Printed at the head of invoices, quotations, credit notes, statements and reports.",
      "contact": "Address and contact"
    },
    "displayName": "Company name",
    "displayNameAr": "Company name in Arabic",
    "displayNameHint": "Required. The legal name below prints on documents when you fill it in.",
    "legalName": "Legal name",
    "legalNameAr": "Legal name in Arabic",
    "vatNumber": "VAT registration number",
    "vatNumberHint": "In Oman: OM followed by 10 digits.",
    "vatNumberOdd": "This isn't the usual shape (OM followed by 10 digits). Check it; you can still save it.",
    "crNumber": "Commercial registration number",
    "address": "Address",
    "addressAr": "Address in Arabic",
    "phone": "Phone",
    "email": "Email",
    "images": {
      "logo": "Company logo",
      "signature": "Authorised signature",
      "stamp": "Company stamp"
    },
    "save": "Save company profile",
    "toast": {
      "saved": "Company profile saved"
    }
  },
  "printing": {
    "footer": {
      "title": "Footer",
      "body": "Printed at the foot of every document, English and Arabic.",
      "en": "Footer (English)",
      "ar": "Footer (Arabic)"
    },
    "terms": {
      "title": "Terms and conditions",
      "body": "One default text for every document, and each type's own where it needs one.",
      "default": "Default terms (every document)",
      "en": "Terms (English)",
      "ar": "Terms (Arabic)",
      "byDocument": "Terms by document",
      "customised": "Customised",
      "usesDefault": "{{document}}: uses the default terms.",
      "customise": "Customise for {{document}}",
      "noDefault": "The default terms are empty, so nothing prints.",
      "reset": "Reset to the default",
      "of": "Terms of {{document}}",
      "ofEn": "{{document}} terms (English)",
      "ofAr": "{{document}} terms (Arabic)",
      "blankUsesDefault": "Leave a language blank to print the default terms in it.",
      "defaultPlaceholder": "Uses the default terms"
    },
    "payment": {
      "title": "Payment details",
      "body": "How customers pay you: the bank accounts printed on documents, and a line of your own.",
      "accounts": "Bank accounts to print",
      "accountsHint": "Up to {{max}}. Bank, account number and IBAN come from each account in Cash & bank, so they stay correct.",
      "noBanks": "No bank accounts yet. Add one in Cash & bank.",
      "noBanksNew": "No bank accounts yet. Add one here: it is ticked to print.",
      "accountNo": "Account",
      "iban": "IBAN",
      "noNumbers": "No account number or IBAN recorded",
      "archived": "Archived: it is left off documents. Untick it.",
      "inactive": "An account you chose is no longer an active bank account. Untick it and save again.",
      "unknownAccount": "An account no longer in use",
      "text": "Payment text",
      "textEn": "Payment text (English)",
      "textAr": "Payment text (Arabic)",
      "textHint": "Printed under the accounts, e.g. “Please quote the invoice number.”"
    },
    "sharing": {
      "title": "Sharing",
      "qr": "QR code on printed invoices",
      "qrHint": "Printed invoices don't carry the QR code yet; this choice is kept for when they do.",
      "qrNone": "No QR code",
      "qrLink": "Link to the online invoice",
      "qrLinkBody": "Printing a posted invoice creates its share link for the code.",
      "linkDays": "Share links expire after",
      "linkDaysHint": "0 means never. Whoever shares a link can still choose another expiry.",
      "linkDaysInvalid": "Enter a whole number of days from 0 to 3650."
    },
    "hijri": {
      "title": "Hijri date",
      "body": "Printed documents can carry the Umm al-Qura Hijri date beside the Gregorian one.",
      "label": "Show the Hijri date",
      "never": "Never",
      "legal": "On legal documents",
      "legalBody": "Contracts, offer letters and official letters.",
      "always": "Always",
      "sampleInvoice": "An invoice",
      "sampleLegal": "A legal document"
    },
    "save": "Save printing",
    "toast": {
      "saved": "Printing settings saved"
    }
  },
  "numbering": {
    "document": "Document",
    "format": "Format",
    "next": "Next number",
    "yearly": "Restarts",
    "everyYear": "Every year ({{year}})",
    "continues": "Never",
    "unused": "Not used yet"
  },
  "defaults": {
    "blocks": {
      "prices": "Prices",
      "dates": "Dates"
    },
    "pricesIncludeTax": "Prices include VAT",
    "pricesIncludeTaxHint": "For new invoices, quotations, credit notes and purchases. On: the price entered is what is paid, VAT included. Off: VAT is added on top.",
    "paymentTerms": "Payment terms",
    "paymentTermsHint": "Invoices and purchases are due this many days after their date, unless another due date is chosen.",
    "paymentTermsInvalid": "Enter a whole number of days from 0 to 365.",
    "quotationValidity": "Quotations are valid for",
    "quotationValidityHint": "A new quotation's valid-until date starts this many days after its date.",
    "quotationValidityInvalid": "Enter a whole number of days from 1 to 365.",
    "discountType": "Default discount type",
    "discountTypeHint": "The type a new line starts with. Every line can still use any of the three.",
    "discountTypes": {
      "PERCENTAGE": "A percentage of the line, e.g. 10%.",
      "PER_UNIT": "An amount off each unit, e.g. 0.500 per unit.",
      "LINE_AMOUNT": "An amount off the whole line, e.g. 5.000 off the line."
    },
    "sentence": {
      "due": "An invoice dated today is due on {{date}}",
      "valid": "a quotation is valid until {{date}}",
      "discount": "new lines use {{type}} discounts",
      "separator": "; ",
      "end": "."
    },
    "impact": {
      "title": "Change how prices are entered?",
      "including": "New documents are entered with prices including VAT. Existing drafts keep their mode.",
      "excluding": "New documents are entered with prices excluding VAT. Existing drafts keep their mode.",
      "confirmIncluding": "Enter prices including VAT",
      "confirmExcluding": "Enter prices excluding VAT"
    },
    "save": "Save defaults",
    "toast": {
      "saved": "Defaults saved"
    }
  },
  "priceLevels": {
    "title": "Price levels",
    "intro": "Each customer buys at Retail, Wholesale or Distributor prices. A price set on the product always wins; when a product has none for the customer's level, it sells at Retail less the percentage below.",
    "wholesale": "Wholesale: % off Retail",
    "wholesaleHint": "When a product has no wholesale price.",
    "distributor": "Distributor: % off Retail",
    "distributorHint": "When a product has no distributor price.",
    "example": "Retail {{retail}} with {{pct}}% → {{price}}",
    "exampleZero": "At 0% it sells at the Retail price.",
    "invalid": "Enter a percentage from 0 to 100, with at most 2 decimals.",
    "save": "Save price levels",
    "toast": {
      "saved": "Price levels saved"
    }
  },
  "payments": {
    "defaultAccounts": "Default account for each type",
    "defaultAccountsBody": "Receipts, refunds and supplier payments start on this account for the type chosen, unless the payment method chosen has an account of its own; anyone who used another account for that type last time starts on theirs. Each type offers only the accounts it fits: cash only a cash account, and a cash account takes no card or bank transfer.",
    "none": "Not set",
    "manageAccounts": "Cash & bank",
    "accountsModuleOff": "Cash & bank is part of Accounting, which is turned off for your company. You can still add, edit and archive the accounts below; balances, opening balances and transfers come back when Accounting is turned on.",
    "tillNote": "Tills take cash, card and the methods you offer at the till, through the drawer and the card account set on each till (or a method's own account).",
    "save": "Save default accounts",
    "toast": {
      "saved": "Default accounts saved"
    },
    "accounts": "Cash and bank accounts",
    "accountsBody": "The cash boxes, bank accounts, card terminals and wallets that payments go to, with the bank details printed on documents. Balances, opening balances and transfers are part of Accounting.",
    "methods": {
      "title": "Your payment methods",
      "body": "The ways your company is paid and pays, by name: Visa, Mastercard, Thawani, a transfer to Bank Muscat. Each has a type that decides the accounting, may start on an account of its own and may be offered at the till. Changes here are saved at once.",
      "add": "Add payment method",
      "showArchived": "Show archived ({{count}})",
      "empty": "No payment methods of your own yet",
      "emptyBody": "Add the cards, wallets and transfers you use, such as Visa, Thawani or a transfer to Bank Muscat. They appear in receipts, refunds, supplier payments and service collections, and at the till when you offer them there.",
      "typeDefault": "Type's default account",
      "atTill": "At the till",
      "archived": "Archived",
      "used_one": "Used in 1 document",
      "used_other": "Used in {{count}} documents",
      "notUsed": "Not used yet",
      "edit": "Edit {{name}}",
      "archive": "Archive {{name}}",
      "restore": "Restore",
      "restoreNamed": "Restore {{name}}",
      "archiveTitle": "Archive {{name}}?",
      "archiveBody": "It leaves the payment forms and the till. Documents recorded with it keep its name, and you can restore it at any time.",
      "archiveConfirm": "Archive",
      "restoreAccount": "{{name}} can't come back on its account: that account is archived or can't take it any more. Edit the method to choose another account, then restore it.",
      "toast": {
        "created": "{{name}} added",
        "updated": "{{name}} saved",
        "archived": "{{name}} archived",
        "restored": "{{name}} restored"
      }
    },
    "methodDialog": {
      "newTitle": "Add payment method",
      "editTitle": "Edit payment method",
      "newSubtitle": "A way your company is paid or pays, such as Visa or Thawani.",
      "name": "Name",
      "namePlaceholder": "e.g. Visa",
      "nameAr": "Name in Arabic",
      "nameArPlaceholder": "مثال: فيزا",
      "type": "Type",
      "typeLocked": "Documents were recorded with this method, so its type stays as it is.",
      "typeHelp": {
        "CASH": "Notes and coins, kept in a cash box.",
        "CARD": "Debit and credit cards, through a card terminal.",
        "BANK_TRANSFER": "Money sent straight to a bank account.",
        "CHEQUE": "A cheque, banked later.",
        "OTHER": "Wallets and payment apps, such as Thawani."
      },
      "account": "Account",
      "accountHint": "The cash or bank account it starts on in payment forms. Without one, the type's default account below is used.",
      "atTill": "Offered at the till",
      "atTillHint": "Shown beside Card when taking payment at the till.",
      "atTillCash": "Not for cash: cash at the till goes into the drawer.",
      "atTillAccounts": "Offered at the till, it goes into a card terminal, bank or wallet account: cash accounts are not listed.",
      "tillAccount": "A method offered at the till can't go into a cash account. Choose a card terminal, bank or wallet account, or the type's default.",
      "save": "Save method",
      "nameTaken": "A payment method with this name already exists.",
      "nameTakenArchived": "An archived payment method already has this name.",
      "nameTakenArchivedHint": "Names stay with archived methods. Rename or restore that one, or choose another name.",
      "showArchived": "Show archived methods",
      "version": "Someone else changed this method since you opened it. Close it and open it again to see their changes."
    }
  },
  "stock": {
    "policy": "When a sale takes stock below zero",
    "policyBody": "Applies when an invoice is posted and when the till completes a sale.",
    "options": {
      "ALLOW": {
        "label": "Allow",
        "body": "Posting and the till go ahead without a word about stock."
      },
      "WARN": {
        "label": "Warn",
        "body": "Posting an invoice lists what is short and can still go ahead; the till sells as usual."
      },
      "BLOCK": {
        "label": "Block",
        "body": "Invoices and till sales that would take stock below zero are refused."
      }
    },
    "defaultWarehouse": "Default warehouse",
    "defaultWarehouseBody": "Sales without a warehouse of their own take stock from the main warehouse.",
    "manageWarehouses": "Warehouses",
    "impact": {
      "blockTitle": "Block sales that take stock below zero?",
      "block": "Invoices and till sales that take stock below zero will be refused from now on. Posted documents are not changed.",
      "blockDrafts": "Drafts that would take stock below zero can't be posted.",
      "blockConfirm": "Block negative stock",
      "allowTitle": "Stop warning about stock?",
      "allow": "No warning will be shown when a sale takes stock below zero.",
      "allowConfirm": "Allow negative stock"
    },
    "save": "Save stock policy",
    "toast": {
      "saved": "Stock policy saved"
    }
  },
  "lock": {
    "date": "Lock postings on or before",
    "dateBody": "Nobody can post, void or reverse anything dated on or before this date. It can't be later than today.",
    "none": "No lock date",
    "clear": "Remove lock date",
    "warning": "Nothing dated on or before {{date}} can be posted or changed.",
    "noLock": "No lock date: any date in an open fiscal year can be posted.",
    "closedYears": "Closed fiscal years",
    "closedYearsBody": "A closed year stays locked; the lock date can't be earlier than its end.",
    "yearRange": "{{from}} – {{to}}",
    "noClosedYears": "No fiscal year is closed.",
    "closedThrough": "Closed through {{date}}.",
    "errors": {
      "future": "The lock date can't be later than today.",
      "closedYear": "The books are closed through {{date}}: the lock date can't be earlier, or removed, while that year is closed.",
      "closedYearNoDate": "A closed fiscal year stays locked: the lock date can't be earlier than its end."
    },
    "impact": {
      "laterTitle": "Lock the books up to {{date}}?",
      "later": "Nothing dated on or before {{date}} can be posted, voided or reversed after this. Documents already posted are not changed.",
      "laterDrafts": "Drafts dated on or before {{date}} can't be posted until you move it.",
      "earlierTitle": "Open periods again?",
      "earlier": "Periods from {{from}} to {{to}} open again for posting.",
      "clearTitle": "Remove the lock date?",
      "clear": "Everything dated up to {{date}} opens again for posting, except in closed fiscal years.",
      "confirm": "Move lock date",
      "clearConfirm": "Remove lock date"
    },
    "save": "Save lock date",
    "toast": {
      "saved": "Books locked up to {{date}}",
      "cleared": "Lock date removed"
    }
  },
  "tasks": {
    "blocks": {
      "workflow": "Review and due dates",
      "overdue": "Overdue work"
    },
    "review": {
      "label": "New tasks need review by default",
      "hint": "Each new task starts with “Needs review” on; whoever creates it can turn it off."
    },
    "dueDate": {
      "label": "Assignees may move a committed due date themselves",
      "hint": "They give a reason. Otherwise they ask the requester or a team lead for a new date."
    },
    "escalation": {
      "label": "Tell the team's leads when a task is overdue by",
      "hint": "Enter 0 to never tell them. Either way, the assignee is reminded every day the task is late.",
      "invalid": "Enter a whole number of days from 0 to 30."
    },
    "save": "Save task settings",
    "toast": {
      "saved": "Task settings saved"
    }
  },
  "projectContracting": {
    "measure": {
      "label": "Measuring work"
    },
    "payment": {
      "label": "Payment of certificates"
    },
    "retention": {
      "label": "Retention"
    },
    "advance": {
      "label": "Advance payment"
    },
    "damages": {
      "label": "Delay damages"
    },
    "approval": {
      "label": "Internal approval of variations and claims"
    },
    "guarantees": {
      "label": "Guarantee reminders and the performance bond"
    }
  },
  "projectInsights": {
    "burnGapPts": {
      "label": "Spend ahead of progress"
    },
    "eacMethod": {
      "label": "Expected final cost"
    },
    "slipToleranceDays": {
      "label": "Finish slipping"
    },
    "arOverdueDays": {
      "label": "Customer pays late"
    },
    "staleDays": {
      "label": "No activity"
    }
  },
  "projects": {
    "features": {
      "title": "Functions",
      "body": "Switch off what the company does not use: its tabs, fields and actions disappear from every project."
    },
    "defaultType": {
      "label": "Type a new project starts from",
      "hint": "Preselected in New project; anyone can pick another or start blank.",
      "none": "None"
    },
    "progressMethod": {
      "label": "Progress of a project without a type",
      "hint": "A project type sets its own method."
    },
    "save": "Save project settings",
    "toast": {
      "saved": "Project settings saved"
    },
    "budgetThresholds": {
      "label": "Budget levels"
    },
    "categoryDefaults": {
      "label": "Cost category when none is chosen"
    },
    "time": {
      "label": "Time rules"
    }
  },
  "service": {
    "priceApproval": {
      "label": "Customer's price approval",
      "body": "Whether a chargeable item’s work is recorded only after the customer agreed its price.",
      "options": {
        "REQUIRED": {
          "label": "Required before work",
          "body": "Technicians record work on an item only once the customer has agreed its price."
        },
        "OPTIONAL": {
          "label": "Optional",
          "body": "Work can be recorded before the price is agreed; the approval can follow."
        }
      }
    },
    "reportPhotos": {
      "label": "Visit photos",
      "body": "Photos attached to the visit, as evidence of the work.",
      "options": {
        "OPTIONAL": {
          "label": "Optional",
          "body": "A visit finishes with or without photos."
        },
        "REQUIRED": {
          "label": "Required to finish",
          "body": "A visit finishes only with at least one photo attached."
        }
      }
    },
    "acknowledgment": {
      "label": "Customer's acknowledgment",
      "body": "The name of the customer who confirmed the visit report.",
      "options": {
        "OPTIONAL": {
          "label": "Optional",
          "body": "A visit finishes with or without the customer’s name."
        },
        "REQUIRED": {
          "label": "Required to finish",
          "body": "A visit finishes only with the name of the customer who acknowledged it."
        }
      }
    },
    "assetRegister": {
      "label": "Sites & assets",
      "body": "Customers' sites hold a tree of places (floors, apartments) and the assets in each; jobs name the units they are about.",
      "switch": "Keep sites, places and assets",
      "on": "Sites & assets shows under Service, and a job can pick a place and its assets.",
      "off": "The screens and job fields are hidden. Nothing recorded is deleted: switching back on shows it again."
    },
    "assetChecklists": {
      "label": "Asset checklists",
      "body": "Technicians check each asset of the job on the visit: its checklist, readings and outcome. A planned job finishes only when every asset is checked.",
      "switch": "Check each asset on the visit",
      "on": "The visit shows each asset's checklist, and a planned job finishes in full only once every asset is checked.",
      "off": "No checklist on the visit, and planned jobs finish like any job. Checks already recorded are kept."
    },
    "save": "Save service settings",
    "toast": {
      "saved": "Service settings saved"
    },
    "visitLead": {
      "label": "Visit lead",
      "body": "Whether a visit is booked with a named lead, or for the team alone.",
      "options": {
        "REQUIRED": {
          "label": "Named when booking",
          "body": "Every visit names its lead; the schedule and the customer know who comes."
        },
        "OPTIONAL": {
          "label": "Optional",
          "body": "A visit may be booked for the team only; the member who starts it becomes its lead."
        }
      }
    },
    "followUpApproval": {
      "label": "Another visit after the first",
      "body": "When work is left after a visit, whether the next visit waits for a service manager.",
      "options": {
        "REQUIRED": {
          "label": "Manager approves",
          "body": "The team asks for a follow-up; a service manager approves it and books the visit."
        },
        "NOT_NEEDED": {
          "label": "Booked at once",
          "body": "The request books the follow-up visit itself: same team and lead, on the proposed date or the next working day."
        }
      }
    },
    "priceApprovalThreshold": {
      "label": "No approval under",
      "body": "With the approval required, a job whose chargeable total (VAT included) is at or under this amount needs none: a call-out fee, a filter change.",
      "field": "Amount (OMR)",
      "hint": "Leave empty to ask for the approval on every chargeable job.",
      "error": "Enter an amount of 0 or more, or leave it empty."
    },
    "simple": {
      "title": "Simple setup",
      "body": "For a small team: visits booked for the team, follow-ups booked at once, no photo or sign-off needed to finish. Each rule can still be set on its own below.",
      "apply": "Use the simple setup",
      "applied": "The simple setup is filled in: save to keep it."
    }
  },
  "contracts": {
    "notice": {
      "label": "Renewal reminders",
      "body": "Those who manage contracts are told before a contract ends, once at each of these days.",
      "day": "Reminder {{n}}, days before the end",
      "days": "days before",
      "remove": "Remove reminder {{n}}",
      "add": "Add a reminder",
      "summary": "Remind {{days}} days before the end.",
      "none": "No reminders: nobody is told before a contract ends.",
      "invalid": "Enter whole days from 1 to 365.",
      "duplicate": "Each number of days once."
    },
    "planAhead": {
      "title": "Planned visits",
      "body": "A contract's plans make a planned job for each visit that falls due, early enough for the office to schedule it.",
      "label": "Make planned jobs",
      "unit": "days before they are due",
      "invalid": "Enter a whole number of days from 1 to 90."
    },
    "billing": {
      "title": "Billing of a new contract",
      "body": "How a new contract's value is split into instalments. Each contract can change it."
    },
    "schedule": {
      "label": "Billing schedule",
      "options": {
        "MONTHLY": "Monthly",
        "QUARTERLY": "Quarterly",
        "HALF_YEARLY": "Half-yearly",
        "YEARLY": "Yearly",
        "UPFRONT": "Upfront (one instalment)",
        "CUSTOM": "Custom (entered by hand)"
      }
    },
    "timing": {
      "label": "When each instalment is billed",
      "options": {
        "ADVANCE": {
          "label": "In advance",
          "body": "Billed on the first day of each period."
        },
        "ARREARS": {
          "label": "In arrears",
          "body": "Billed on the last day of each period."
        }
      }
    },
    "terms": {
      "title": "Terms of a new contract",
      "body": "What a new contract covers until it says otherwise. Each contract can change its own terms.",
      "labour": {
        "label": "Labour",
        "options": {
          "INCLUDED": {
            "label": "Included",
            "body": "Work on covered assets isn't charged."
          },
          "EXCLUDED": {
            "label": "Billed",
            "body": "Work is charged on every job."
          }
        }
      },
      "parts": {
        "label": "Parts",
        "options": {
          "INCLUDED": {
            "label": "Included",
            "body": "Parts fitted to covered assets aren't charged."
          },
          "EXCLUDED": {
            "label": "Billed",
            "body": "Parts are charged."
          },
          "CAPPED": {
            "label": "Included up to a cap",
            "body": "Covered up to an amount; past it, parts are charged."
          }
        }
      },
      "partsCap": {
        "label": "Parts cap",
        "missing": "Enter the parts cap, above zero."
      },
      "partsCapBasis": {
        "label": "The cap counts",
        "options": {
          "PER_JOB": "Per job",
          "PER_YEAR": "Per contract year"
        },
        "missing": "Per job or per contract year?"
      },
      "consumables": {
        "label": "Consumables",
        "options": {
          "INCLUDED": {
            "label": "Included",
            "body": "Filters, gas and other consumables aren't charged."
          },
          "EXCLUDED": {
            "label": "Billed",
            "body": "Consumables are charged."
          }
        }
      },
      "callOuts": {
        "label": "Call-outs",
        "options": {
          "INCLUDED": {
            "label": "Included",
            "body": "Breakdown visits the customer asks for aren't charged, up to the allowance."
          },
          "EXCLUDED": {
            "label": "Billed",
            "body": "Each call-out is charged."
          }
        }
      },
      "callOutsPerYear": {
        "label": "Call-outs a year",
        "hint": "Leave empty for unlimited.",
        "invalid": "Enter a whole number from 1 to 365, or leave it empty."
      },
      "responseHours": {
        "label": "Response time",
        "unit": "hours",
        "hint": "Optional: the response promised to the customer, shown on the contract's jobs.",
        "invalid": "Enter whole hours from 1 to 720, or leave it empty."
      },
      "specialTerms": {
        "label": "Special terms",
        "hint": "What the common terms don't say; printed on the contract."
      }
    },
    "consumables": {
      "label": "Consumable categories",
      "body": "Catalogue products in these categories count as consumables on a contract job (filters, gas, cleaning chemicals). Other goods count as parts.",
      "placeholder": "Choose categories",
      "search": "Search categories",
      "empty": "No categories",
      "clear": "Clear the categories",
      "chosen": "Categories chosen: {{n}}",
      "needs": "Choosing categories needs “{{permission}}”.",
      "tooMany": "Up to 50 categories."
    },
    "save": "Save contract settings",
    "toast": {
      "saved": "Contract settings saved"
    }
  },
  "calendar": {
    "week": {
      "title": "Working week",
      "hint": "Which days are worked, and the working hours of each. Timers set in working hours (approval deadlines, for example) run only within these hours.",
      "count_one": "{{n}} working day",
      "count_other": "{{n}} working days",
      "start": "{{day}} starts at",
      "end": "{{day}} ends at",
      "off": "Day off",
      "save": "Save working week",
      "saved": "Working week saved",
      "errors": {
        "none": "At least one day of the week must be a working day.",
        "order": "A working day must start before it ends.",
        "orderOn": "{{day}} must start before it ends."
      }
    },
    "conflict": {
      "title": "Someone else changed the working week",
      "body": "Reload to see their version, then make your change again.",
      "reload": "Reload"
    },
    "holidays": {
      "title": "Holidays",
      "hint": "Days off besides the weekly ones. Mark a date provisional while it depends on the moon sighting.",
      "add": "Add holiday",
      "edit": "Edit",
      "delete": "Delete",
      "actionsFor": "Actions for {{name}}",
      "previousYear": "Previous year",
      "nextYear": "Next year",
      "year": "{{year}}",
      "tableLabel": "Holidays in {{year}}",
      "count_one": "{{n}} holiday",
      "count_other": "{{n}} holidays",
      "provisionalCount_one": "{{n}} provisional",
      "provisionalCount_other": "{{n}} provisional",
      "empty": "No holidays in {{year}}",
      "emptyHint": "Add them one by one or import a CSV file."
    },
    "fields": {
      "date": "Date",
      "days": "Number of days",
      "name": "Name",
      "nameAr": "Arabic name"
    },
    "provisional": {
      "label": "Provisional",
      "badge": "Provisional",
      "hint": "The date may still move (for example, Eid depends on the moon sighting)."
    },
    "dialog": {
      "addTitle": "Add holiday",
      "editTitle": "Edit holiday",
      "add": "Add holiday",
      "save": "Save holiday",
      "until": "Until {{date}}"
    },
    "delete": {
      "title": "Delete {{name}}?",
      "body": "{{date}} becomes a normal day again. Due dates are worked out again.",
      "confirm": "Delete holiday"
    },
    "toast": {
      "added_one": "Holiday added",
      "added_other": "{{count}} holiday days added",
      "updated": "Holiday saved",
      "deleted": "{{name}} deleted"
    },
    "errors": {
      "HOLIDAY_DATE_TAKEN": "Another holiday is already on this date.",
      "stale": "Someone else changed this holiday. Close the dialog and open it again.",
      "date": "Choose a date between 2000 and 2100.",
      "days": "Enter a whole number of days from 1 to {{max}}."
    },
    "import": {
      "action": "Import CSV",
      "title": "Import holidays",
      "hint": "All the rows are added, or none of them.",
      "file": "CSV file",
      "paste": "Or paste the rows",
      "format": "One holiday per line: date, name, Arabic name, provisional (yes/no). Dates as 2026-11-18 or 18/11/2026.",
      "replace": "Replace the holidays of {{year}}",
      "syntax": "The file can't be read: check the quotes on line {{line}}.",
      "tooMany": "At most {{max}} rows can be imported at once.",
      "problems_one": "{{n}} row needs fixing before importing.",
      "problems_other": "{{n}} rows need fixing before importing.",
      "allGood_one": "{{n}} row ready to import.",
      "allGood_other": "{{n}} rows ready to import.",
      "confirm_one": "Import {{n}} row",
      "confirm_other": "Import {{n}} rows",
      "previewLabel": "Rows to import",
      "line": "Line",
      "check": "Check",
      "ok": "Ready",
      "done_one": "{{n}} holiday imported",
      "done_other": "{{n}} holidays imported",
      "issues": {
        "date": "Date not readable",
        "name": "Name missing",
        "duplicate": "Date repeated in the file",
        "taken": "Already a holiday",
        "outsideYear": "Outside the year"
      }
    }
  },
  "notifications": {
    "email": "E-mail sender",
    "emailBody": "E-mails go out from the platform's address with your company's name.",
    "fromName": "Sender name",
    "fromNameHint": "What customers see as the sender, e.g. “Al Noor Trading”. Empty: your company name.",
    "replyTo": "Reply-to address",
    "replyToHint": "Where customers' replies go. Empty: replies go to the sender address.",
    "whatsapp": "WhatsApp",
    "whatsappEnabled": "Send WhatsApp messages",
    "whatsappHint": "Needs the WhatsApp API key below.",
    "key": {
      "label": "WhatsApp API key",
      "body": "Kept encrypted and never shown again once saved, not even to you.",
      "set": "Set",
      "setOn": "Set on {{date}}",
      "setOnBy": "Set on {{date}} by {{name}}",
      "setOnByYou": "Set on {{date}} by you",
      "notSet": "Not set",
      "setAction": "Set key",
      "replace": "Replace key",
      "remove": "Remove key",
      "needs": "Setting or removing the key needs “Manage secrets”.",
      "setTitle": "Set the {{name}}",
      "replaceTitle": "Replace the {{name}}",
      "dialogBody": "Paste the key from your WhatsApp provider. It is sent once, stored encrypted and never shown again.",
      "removeTitle": "Remove the WhatsApp API key?",
      "removeBody": "WhatsApp messages can't be sent until a key is set again.",
      "toast": {
        "set": "WhatsApp API key set",
        "replaced": "WhatsApp API key replaced",
        "removed": "WhatsApp API key removed"
      }
    },
    "save": "Save messaging",
    "toast": {
      "saved": "Messaging settings saved"
    },
    "notReady": {
      "title": "E-mail is not set up",
      "body": "Customers will not receive e-mails until delivery is set up.",
      "link": "Set up delivery"
    },
    "sender": "Sender",
    "senderBody": "The name and the reply address customers see on every e-mail.",
    "delivery": {
      "title": "E-mail delivery",
      "body": "Which mail service sends your e-mails.",
      "PLATFORM": {
        "label": "Sahab mail service",
        "body": "Sent from the platform's address with your company's name. Nothing to set up."
      },
      "SMTP": {
        "label": "Your own mail server (SMTP)",
        "body": "Sent from your company's address through your mail server. Needs its details below."
      }
    },
    "smtp": {
      "host": "Server host",
      "hostHint": "For example smtp.office365.com",
      "port": "Port",
      "portHint": "Usually 587 with STARTTLS, or 465 with TLS.",
      "security": "Encryption",
      "securityModes": {
        "STARTTLS": "STARTTLS (usually port 587)",
        "TLS": "TLS (usually port 465)"
      },
      "username": "User name",
      "fromAddress": "Sender address",
      "fromAddressHint": "The address e-mails are sent from, e.g. accounts@yourcompany.om.",
      "required": "Required when e-mail goes through your own server",
      "invalidHost": "Enter a host name such as smtp.example.com",
      "invalidPort": "Use port 25, 465, 587 or 2525",
      "hostNotAllowed": "This host is on a private network. Enter your mail server’s public host name."
    },
    "password": {
      "label": "SMTP password",
      "body": "Kept encrypted and never shown again once saved, not even to you.",
      "setAction": "Set password",
      "replace": "Replace password",
      "remove": "Remove password",
      "needs": "Setting or removing the password needs “Manage secrets”.",
      "dialogBody": "Type the password of the mail server user. It is sent once, stored encrypted and never shown again.",
      "removeTitle": "Remove the SMTP password?",
      "removeBody": "E-mails can't go through your server until a password is set again.",
      "toast": {
        "set": "SMTP password set",
        "replaced": "SMTP password replaced",
        "removed": "SMTP password removed"
      }
    },
    "residency": {
      "title": "Your data is kept in Oman",
      "body": "E-mail may only go through a mail service approved for keeping data in Oman.",
      "approve": "This mail server is approved for keeping data in Oman",
      "approveHint": "Tick only if the server is hosted in Oman or your agreement allows it.",
      "notAllowed": "Not allowed where your company's data is kept. Choose another service or confirm the server is approved."
    },
    "test": {
      "title": "Test e-mail",
      "body": "Send a short e-mail to yourself to check that delivery works. It uses the saved settings.",
      "to": "To you at",
      "language": "Language",
      "send": "Send test e-mail",
      "sendAgain": "Send another",
      "notReady": "Set up e-mail delivery first.",
      "dirty": "Save your changes first: the test uses the saved settings.",
      "QUEUED": "Queued. It goes out in a moment…",
      "SENDING": "Sending…",
      "SENT": "Sent.",
      "FAILED": "Not sent.",
      "SUPPRESSED": "Not sent.",
      "sent": "Sent to {{address}} at {{time}}. Check your inbox, and the spam folder if it isn't there.",
      "stillQueued": "Still queued. Delivery can take a few minutes.",
      "refresh": "Refresh",
      "openOutbox": "Open the outbox",
      "failedPrefix": "Not sent:"
    },
    "whatsappLater": "WhatsApp: available in a later release"
  },
  "mappingImpact": {
    "title_one": "Change {{n}} account mapping?",
    "title_other": "Change {{n}} account mappings?",
    "future": "Future postings go to the new account.",
    "balances": "Balances already on the old account stay there; move them with a journal entry if needed.",
    "confirm": "Change account mapping"
  },
  "modules": {
    "crm": {
      "description": "Customers and customer groups."
    },
    "sales": {
      "description": "Quotations, invoices, credit notes, receipts, refunds and receivables, with customer balances and statements."
    },
    "pos": {
      "description": "The till, shifts and till sales."
    },
    "purchasing": {
      "description": "Suppliers, purchases, supplier payments, returns and payables."
    },
    "catalog": {
      "description": "Products, variants, categories, units and attributes."
    },
    "inventory": {
      "description": "Warehouses, stock levels, movements, adjustments and transfers."
    },
    "accounting": {
      "description": "The ledger, journal entries, fiscal years and the lock date, cash and bank transfers and reconciliation, and financial reports."
    },
    "expenses": {
      "description": "Expenses and their categories."
    },
    "workflows": {
      "description": "Configurable approval routes for documents (purchases today), My approvals, delegation and the request monitor."
    },
    "tasks": {
      "description": "Tasks with teams, assignees, deadlines and review; My work and the task inbox."
    },
    "service": {
      "description": "Service jobs, team visits, price approvals, invoicing of completed work and collections the accountant verifies."
    },
    "projects": {
      "description": "Projects with their plan, team, budget, costs from purchases, expenses and stock, billing through Sales, contracting (BOQ, progress claims, retention) and insights."
    },
    "ai": {
      "description": "The AI assistant, under the company’s own OpenAI key: questions about a project, status report drafts, explanations and plan breakdowns. Nothing changes without a click."
    },
    "loyalty": {
      "description": "Loyalty points: members earn on what they buy, redeem at the till, climb tiers and see points expire; with bonus rules, hand adjustments and a points report."
    },
    "contracts": {
      "description": "Maintenance contracts: what each customer's sites have covered and on what terms, planned maintenance visits, per-asset checklists and the contract's instalments."
    },
    "hr": {
      "description": "Human resources: the employee file under the Omani Labour Law, contracts, salaries made of pay components, documents with their expiries and the status history."
    },
    "payroll": {
      "description": "Payroll: monthly runs over the employee file with Social Protection Fund contributions, end-of-service gratuity, payslips, the salary journal and the WPS salary file."
    }
  },
  "modulesList": {
    "on": "On",
    "off": "Off"
  },
  "regional": {
    "dates": "Dates",
    "datesBody": "Written with the month's name, so they can't be misread.",
    "digits": "Digits",
    "digitsBody": "Latin digits in English and Arabic, as Omani businesses write amounts.",
    "currency": "Currency",
    "currencyName": "Omani rial (<rial/>), 3 decimals:",
    "currencyBody": "Every amount is kept to the baisa.",
    "hijri": "Hijri date",
    "hijriBody": "Umm al-Qura calendar. On printed documents: {{mode}} (changed in Printing).",
    "timeZone": "Time zone",
    "timeZoneBody": "Business days and document dates follow Muscat time.",
    "language": "Language",
    "languageBody": "Each person picks English or Arabic for themselves; documents print in the language of whoever prints them, with the other language alongside."
  },
  "system": {
    "version": "App version",
    "environment": "Environment",
    "production": "Production",
    "development": "Development",
    "support": "Support",
    "supportBody": "For help with your account, your modules or a problem in the app, contact your Sahab ERP provider and quote the app version above."
  },
  "appearance": {
    "reset": "Reset to default",
    "resetDone": "Appearance reset to default",
    "scope": "Only you see these choices, and only on this browser. Printed and shared documents keep the company’s paper look.",
    "style": {
      "title": "Style",
      "body": "The overall look of the app.",
      "modern": {
        "label": "Modern",
        "body": "A dark side rail, warm paper pages and crisp white panels: the clean look of the cloudTech apps."
      },
      "nimbus": {
        "label": "Nimbus",
        "body": "Frosted glass over a drifting cloud field, with a glowing accent."
      },
      "ledger": {
        "label": "Classic ledger",
        "body": "Flat paper surfaces and ruled lines, the calm look of an account book."
      }
    },
    "mode": {
      "title": "Light or dark",
      "body": "Same as system follows your computer’s setting.",
      "light": "Light",
      "dark": "Dark",
      "system": "Same as system"
    },
    "accent": {
      "title": "Accent colour",
      "body": "Used for primary buttons, links, focus and the current page.",
      "jade": "Jade",
      "stamp": "Stamp ink",
      "sky": "Sky",
      "teal": "Gulf teal",
      "saffron": "Saffron",
      "rose": "Rose",
      "violet": "Violet"
    },
    "corners": {
      "title": "Corners",
      "body": "How rounded buttons, fields and panels are.",
      "sharp": "Sharp",
      "soft": "Soft",
      "round": "Round"
    },
    "density": {
      "title": "Table rows",
      "body": "Compact fits more rows on screen; comfortable is easier to scan.",
      "compact": "Compact",
      "comfortable": "Comfortable"
    },
    "ambient": {
      "title": "Moving background",
      "body": "The clouds behind the menu drift slowly. Turn this off for a still background.",
      "nimbusOnly": "Available with the Nimbus style.",
      "label": "Let the clouds drift"
    }
  },
  "teams": {
    "views": "Team views",
    "active": "Active",
    "archived": "Archived",
    "new": "New team",
    "listLabel": "Teams",
    "lead": "Lead",
    "memberCount_one": "{{n}} member",
    "memberCount_other": "{{n}} members",
    "noMembers": "No members yet.",
    "openWorkspace": "Open the team’s work space",
    "editNamed": "Edit {{name}}",
    "archiveNamed": "Archive {{name}}",
    "restoreNamed": "Restore {{name}}",
    "empty": {
      "title": "No teams yet",
      "body": "Create a team, add its people and choose its leads. Tasks and service visits are then given to teams.",
      "archived": "No archived teams."
    }
  },
  "outbox": {
    "intro": "Every e-mail the system sent or tried to send, newest first. Addresses are partly hidden.",
    "tabsLabel": "Messages by status",
    "tabs": {
      "all": "All",
      "FAILED": "Failed",
      "QUEUED": "Queued",
      "SUPPRESSED": "Suppressed"
    },
    "searchLabel": "Search messages",
    "searchPlaceholder": "Search by recipient or template",
    "clearSearch": "Clear search",
    "tableLabel": "Messages",
    "columns": {
      "when": "Created",
      "message": "Message",
      "recipient": "Recipient",
      "record": "Record",
      "status": "Status"
    },
    "status": {
      "QUEUED": "Queued",
      "SENDING": "Sending",
      "SENT": "Sent",
      "FAILED": "Failed",
      "SUPPRESSED": "Suppressed"
    },
    "locale": {
      "en": "In English",
      "ar": "In Arabic"
    },
    "recipientRemoved": "Address removed",
    "attempts_one": "{{count}} attempt",
    "attempts_other": "{{count}} attempts",
    "empty": "No messages sent yet",
    "emptyBody": "E-mails appear here once the system sends one, for example an invoice or a test e-mail.",
    "emptyFiltered": "No messages match these filters.",
    "rowActions": "Message actions",
    "retry": "Send again",
    "retryTitle": "Send this message again?",
    "retryBody": "It goes back in the queue and is sent to {{recipient}} with the same content.",
    "toast": {
      "retried": "Message queued again"
    },
    "clearFilters": "Clear filters"
  },
  "templates": {
    "intro": "The e-mails the system sends for you, in English and Arabic. Change their wording to your company's own.",
    "tableLabel": "Message templates",
    "empty": "No message templates yet",
    "columns": {
      "template": "Template",
      "kind": "Kind",
      "state": "Wording",
      "changed": "Last changed"
    },
    "category": {
      "TRANSACTIONAL": "Service message",
      "MARKETING": "Marketing",
      "NURTURE": "Follow-up"
    },
    "state": {
      "default": "Default",
      "customised": "Customised",
      "attention": "Needs attention",
      "unsaved": "Unsaved"
    },
    "missingCount_one": "{{count}} variable left out",
    "missingCount_other": "{{count}} variables left out",
    "back": "All templates",
    "editorIntro": "Edit the subject and the text in both languages. Variables in double braces are filled in when the e-mail is sent.",
    "language": {
      "en": "English",
      "ar": "Arabic"
    },
    "subject": "Subject",
    "body": "Text",
    "bodyHint": "Leave an empty line between paragraphs.",
    "variables": "Variables",
    "variablesHint": "Insert one where the cursor was in the subject or the text.",
    "insert": "Insert {{name}}",
    "inserted": "Inserted {{name}}",
    "preview": "Preview with sample values",
    "unknownTitle": "Remove the variables this e-mail doesn't have",
    "unknownItem": "{{language}} uses",
    "missingTitle": "Some variables are left out",
    "missingItem": "{{language}} doesn't use",
    "blockedUnknown": "Remove the unknown variables to save.",
    "blockedEmpty": "Each language needs a subject and a text.",
    "unsaved": "Unsaved changes: {{languages}}",
    "allSaved": "All changes saved",
    "discard": "Discard",
    "save": "Save template",
    "reset": "Reset to default",
    "resetTitle": "Reset the {{language}} text to the default?",
    "resetBody": "Your wording is replaced by the text that comes with the product. Earlier versions stay in the history.",
    "staleTitle": "Someone else changed this template",
    "staleBody": "Your changes are kept here. Copy them, go back to the list and open the template again to see the latest text.",
    "toast": {
      "saved": "Template saved",
      "reset": "{{language}} text reset to the default"
    }
  },
  "ai": {
    "usageLink": "Usage and runs",
    "available": "Available",
    "unavailable": "Not available",
    "save": "Save AI settings",
    "toast": {
      "saved": "AI settings saved"
    },
    "blocked": {
      "disabled": {
        "title": "Switched off by your Sahab provider",
        "body": "Your Sahab provider has the assistant switched off. Only they can switch it on."
      },
      "module_off": {
        "title": "The AI assistant module is off",
        "body": "Your Sahab provider switches the module on for your company. Ask them if you want to use it."
      },
      "no_policy": {
        "title": "No AI policy for your company yet",
        "body": "Your Sahab provider sets the monthly token cap and the models in their console. Ask them to set it."
      },
      "suspended": {
        "title": "Suspended by your Sahab provider",
        "body": "The provider suspended the assistant for your company. Ask them why and when it can resume."
      },
      "not_enabled": {
        "title": "The assistant is off",
        "body": "Turn it on below. You'll first read what is sent to OpenAI and accept it."
      },
      "no_key": {
        "title": "No OpenAI key",
        "body": "Set your company's OpenAI key below. The assistant runs under your own OpenAI account."
      },
      "residency": {
        "title": "Waiting for the residency approval",
        "body": "Your company's data is kept in Oman. Approve sending it to OpenAI outside Oman below, or keep the assistant off."
      },
      "not_allowed": {
        "title": "Not allowed for this account",
        "body": "Impersonated sessions and system accounts never run the assistant."
      }
    },
    "enabled": {
      "title": "Assistant",
      "body": "When it is on, people with “Use the AI assistant” can use it on the records they can already open.",
      "label": "Turn on the AI assistant"
    },
    "ack": {
      "title": "Before you turn on the AI assistant",
      "intro": "Read what the assistant sends and under whose account. Your company accepts this once.",
      "points": {
        "sent": {
          "title": "What is sent to OpenAI",
          "body": "The questions people ask, and what the assistant reads to answer them: record codes, names, statuses, dates and amounts. Not contact details, addresses, ID numbers, attachments or notes about people."
        },
        "account": {
          "title": "Under whose account",
          "body": "Everything goes under your company's own OpenAI account and key, on the terms between your company and OpenAI."
        },
        "controls": {
          "title": "What to set at OpenAI",
          "body": "Set a monthly spend limit and the data controls (how long OpenAI keeps data, and whether it may use it) in your OpenAI account."
        },
        "clicks": {
          "title": "Nothing changes without a click",
          "body": "The assistant only reads. A change it proposes happens only when a person confirms it."
        },
        "recorded": {
          "title": "Recorded",
          "body": "Your acceptance is recorded with your name and the time."
        }
      },
      "version": "Acknowledgement version {{version}}",
      "accept": "Accept and turn on",
      "close": "Close",
      "pending": "Accepted. The assistant turns on when you save.",
      "accepted": "Accepted on {{date}} (version {{version}})",
      "acceptedBy": "Accepted by {{name}} on {{date}} (version {{version}})",
      "acceptedByYou": "Accepted by you on {{date}} (version {{version}})",
      "read": "Read it again",
      "newVersion": "The acknowledgement has changed since. Turning the assistant on again asks for it first."
    },
    "docAck": {
      "title": "Before you turn on a feature that reads documents",
      "intro": "A feature that reads documents sends more than the rest of the assistant. Your company accepts this once, before the first such feature is turned on.",
      "sentBody": "The questions people ask, and what the assistant reads to answer them: record codes, names, statuses, dates and amounts. Not contact details, addresses, ID numbers, attachments or notes about people, except features that read documents, which say so under their own tick.",
      "documentsTitle": "Documents it reads",
      "documentsBody": "A feature that reads documents, such as filling a purchase from a supplier’s bill, sends what it is given: the whole document goes to OpenAI with everything on it, names, addresses, phone and tax numbers included. Nothing of the document itself is kept in Sahab after the reading. What people type with it stays in the assistant history for the retention period, like any question, and so does what the assistant on every page reads from a document sent in its panel (its lines, figures and names), so a job can carry on.",
      "accept": "Accept and tick it",
      "required": "Turning on a feature that reads documents needs the documents acknowledgement first."
    },
    "key": {
      "title": "Company OpenAI key",
      "body": "The assistant runs under your company's own OpenAI account. The key is kept encrypted and never shown again, not even to you.",
      "set": "Set",
      "setOn": "Set on {{date}}",
      "notSet": "Not set",
      "setAction": "Set key",
      "replace": "Replace key",
      "remove": "Remove key",
      "check": "Check the key",
      "checkOk": "The key works. Models it can use:",
      "checkNoModels": "The key works, but it can't use any of the models allowed here. Check the key's model access in your OpenAI account.",
      "spendLimit": "Set a monthly spend limit and data controls for this key in your OpenAI account.",
      "setTitle": "Set the OpenAI key",
      "replaceTitle": "Replace the OpenAI key",
      "dialogBody": "Paste a key from your company's OpenAI account. It is sent once, stored encrypted and never shown again.",
      "removeTitle": "Remove the OpenAI key?",
      "removeBody": "The assistant stops for everyone until a key is set again.",
      "toast": {
        "set": "OpenAI key set",
        "replaced": "OpenAI key replaced",
        "removed": "OpenAI key removed"
      }
    },
    "model": {
      "title": "Model",
      "body": "The OpenAI model the assistant uses. Your Sahab provider decides which models are allowed.",
      "label": "Model",
      "hint": "Default: the first allowed model.",
      "default": "Default ({{model}})",
      "none": "No model is allowed yet. Your Sahab provider chooses the models the assistant may use.",
      "gone": "{{model}} (no longer allowed)",
      "goneBody": "{{model}} is no longer allowed. The assistant uses {{fallback}} until you choose another.",
      "notAllowed": "This model isn't allowed for your company. Choose one from the list."
    },
    "limits": {
      "title": "Limits",
      "body": "They protect your company's OpenAI bill. A request that would go over the monthly limit is refused.",
      "monthly": "Monthly token limit",
      "monthlyHint": "At most {{cap}}, the cap your Sahab provider set. Empty: that cap.",
      "monthlyHintNoCap": "Your Sahab provider hasn't set a cap yet. Empty: no limit of your own.",
      "tokensUnit": "tokens a month",
      "daily": "Requests per person a day",
      "dailyHint": "1 to 500. Each question, draft or explanation counts as one.",
      "runsUnit": "a day",
      "invalidTokens": "Enter a whole number of tokens",
      "invalidDaily": "Enter a whole number from 1 to 500",
      "aboveCap": "At most {{cap}} tokens a month, the cap your Sahab provider set",
      "aboveCapUnknown": "Above the cap your Sahab provider set",
      "tokensExplained": "A token is about ¾ of an English word (fewer for Arabic); a typical question uses 5,000–15,000 tokens. Your OpenAI account shows what they cost."
    },
    "features": {
      "title": "Features",
      "body": "Each one can be turned on or off. People still need their usual permission on the record.",
      "assistant": {
        "ask": {
          "label": "The assistant on every page",
          "body": "An Assistant button in the top bar of every page except the till. It looks up sales, customers, suppliers, purchases, expenses and products, runs the sales and purchase reports, opens pages, and answers from the stock, the books and the service sites where their Ask feature is on here, always as far as each person may see. Changes still come as cards for someone to confirm."
        }
      },
      "projects": {
        "ask": {
          "label": "Ask about a project",
          "body": "People ask questions about a project they can open. The assistant reads its figures and can propose a change, which happens only when they confirm it."
        },
        "status_draft": {
          "label": "Draft the weekly status report",
          "body": "Drafts the four sections of a project's weekly report from the week's record. Figures it can't trace to a record are marked Unverified."
        },
        "explain_signal": {
          "label": "Explain an alert",
          "body": "Explains in plain words why a project alert was raised, and suggests up to three next steps."
        },
        "plan": {
          "label": "Break a scope into phases",
          "body": "Turns a scope description into phases, milestones and tasks that the person edits before adding them to the plan."
        },
        "boq_mapping": {
          "label": "Map BOQ import columns",
          "body": "Suggests which column of a BOQ file is which field. Only the header and the first five rows are sent."
        }
      },
      "accounting": {
        "ask": {
          "label": "Ask about the books",
          "body": "“Ask the assistant” on the reports, the chart of accounts, journals and Books health: answers with links, and tables you can copy as CSV."
        },
        "explain": {
          "label": "Explain a report figure",
          "body": "The ✦ on a report line: the figure’s biggest movements from the books first, then the assistant’s explanation."
        },
        "review": {
          "label": "Review Books health",
          "body": "“Review with the assistant” on Books health: the month’s findings explained and ranked, with tasks and dismissals proposed as cards."
        },
        "coding": {
          "label": "Suggest how to code",
          "body": "Suggests the category of a new expense from a new payee (a known payee’s own history needs no AI), and matches or adjustments for bank statement lines."
        },
        "setup": {
          "label": "Suggest setup",
          "body": "“Suggest…” on expense categories, the chart of accounts and account mapping: categories, accounts and mappings as cards confirmed one by one."
        },
        "commentary": {
          "label": "Draft the management commentary",
          "body": "“Draft commentary” on the profit and loss: a monthly commentary from the books, with figures it can’t vouch for marked “Unverified”."
        }
      },
      "inventory": {
        "ask": {
          "label": "Ask about the stock",
          "body": "“Ask the assistant” on stock levels, movements, adjustments, transfers, warehouses and the stock reports: answers with links, and tables you can copy as CSV."
        },
        "explain": {
          "label": "Explain a product",
          "body": "The ✦ on a stock row: the product’s figures from the stock records first, then the assistant’s explanation of what moved its quantity and cost. When this is off, the figures stay on the row as “Stock insight”."
        },
        "review": {
          "label": "Review the stock",
          "body": "“Review stock” on Stock levels: the stock checks ranked, with tasks, draft transfers and reorder levels proposed as cards."
        },
        "reorder": {
          "label": "Suggest a reorder",
          "body": "“Suggest a reorder” on Low stock and Stock levels: what to buy and how much, with a draft purchase per supplier or a draft transfer proposed as cards. Nothing is ordered or moved until someone posts the draft."
        }
      },
      "purchasing": {
        "capture": {
          "label": "Fill a purchase from a list or a document",
          "body": "“Fill from a list or a document” on New purchase: the assistant reads a typed list, or photos or a PDF of the supplier’s bill, and fills the form for the user to check and save. The whole document goes to OpenAI with everything written on it, the supplier’s address, phone and tax number included. The document isn’t kept; what the user types stays in the assistant history for the retention period, and the copy attached when the user saves stays with the purchase."
        }
      },
      "service": {
        "ask": {
          "label": "Set up sites by asking",
          "body": "“Ask the assistant” on Sites & assets and on a site page: answers about sites, places and equipment, and proposes new sites, places to generate, a place to duplicate and equipment to add as cards. Nothing is added until someone confirms a card. Occupants, addresses and phones are never sent."
        },
        "capture": {
          "label": "Fill a site from a list or a document",
          "body": "“Fill from a list or a document” on a site page: the assistant reads a typed equipment list, or photos or a PDF of a handover schedule or survey, and opens the site’s import preview for the user to check and apply. The whole document goes to OpenAI with everything written on it, occupants’ names and phones included. The document isn’t kept; the file the assistant writes stays with the site, as an imported file does."
        }
      }
    },
    "retention": {
      "title": "Retention",
      "body": "How long conversations are kept after their last activity.",
      "label": "Keep conversations for",
      "hint": "1 to 365 days.",
      "invalid": "Enter a whole number of days from 1 to 365",
      "purgedTitle": "Deleted after that",
      "purged": "The questions, the answers and any proposals still waiting for a decision.",
      "keptTitle": "Kept",
      "kept": "Each request's record (who, when, which feature and record, the model, tokens and outcome), for 24 months, without any text."
    },
    "residency": {
      "title": "Residency approval",
      "body": "Your company's data is kept in Oman. The assistant sends questions and the records it reads to OpenAI, outside Oman.",
      "approve": "Approve sending this data to OpenAI outside Oman",
      "hint": "Your approval is recorded with your name and the time when you save.",
      "approved": "Approved on {{date}}",
      "approvedBy": "Approved by {{name}} on {{date}}",
      "approvedByYou": "Approved by you on {{date}}"
    },
    "errors": {
      "AI_DISABLED": "Your Sahab provider has the AI assistant switched off.",
      "AI_BLOCKED_BY_POLICY": "The AI assistant isn't available for your company right now.",
      "AI_KEY_MISSING": "Your company's OpenAI key isn't set. Set it in Settings › AI assistant.",
      "AI_ACK_REQUIRED": "Accept the acknowledgement to turn the AI assistant on.",
      "AI_LIMIT_ABOVE_CAP": "The monthly limit is above the cap your Sahab provider set.",
      "AI_MODEL_NOT_ALLOWED": "This model isn't allowed for your company. Choose another one.",
      "AI_BUDGET_EXCEEDED": "The token limit was reached.",
      "AI_RUN_IN_PROGRESS": "Another request of the same person was still running.",
      "AI_RUN_EXPIRED": "Nobody decided on the proposals within 24 hours.",
      "AI_RESUME_INCOMPATIBLE": "The assistant changed while it waited for a decision.",
      "AI_NOT_ALLOWED": "This account can't use the AI assistant.",
      "AI_PROVIDER_AUTH": "OpenAI refused the key. Check that it is correct and active, or replace it.",
      "AI_PROVIDER_UNAVAILABLE": "OpenAI couldn't be reached. Try again in a few minutes.",
      "AI_PROVIDER_RATE_LIMITED": "OpenAI is limiting requests on this key. Try again in a minute.",
      "AI_TURN_LIMIT": "The assistant needed too many steps and stopped.",
      "AI_MODEL_ERROR": "The model returned something unusable.",
      "AI_TIMEOUT": "The request took too long and was stopped."
    },
    "usage": {
      "title": "Usage and runs",
      "intro": "Tokens used against your monthly limit, and each request of the assistant. Only who, when and how much: never a question or an answer.",
      "month": "Month",
      "usedOf": "tokens used of the {{limit}} limit ({{percent}})",
      "usedNoLimit": "tokens used. No monthly limit applies.",
      "reserved": "Held by running requests",
      "reservedHint": "Running requests and those waiting for a decision hold tokens until they finish; then only what they used counts.",
      "runs": "Requests",
      "meter": "Tokens used this month",
      "meterText": "{{used}} of {{limit}} tokens ({{percent}})",
      "byDay": "Tokens by day",
      "dayByDay": "Day by day",
      "day": "Day",
      "byFeature": "By feature",
      "feature": "Feature",
      "byPerson": "By person",
      "person": "Person",
      "runsColumn": "Requests",
      "tokensColumn": "Tokens",
      "noneThisMonth": "No requests this month."
    },
    "runs": {
      "title": "Requests",
      "intro": "What each request was about and how it ended. The text stays with the person who asked.",
      "tableLabel": "Assistant requests",
      "filters": {
        "status": "Status",
        "allStatuses": "All statuses",
        "feature": "Feature",
        "allFeatures": "All features",
        "person": "Person",
        "allPeople": "Everyone",
        "chosenPerson": "Chosen person"
      },
      "columns": {
        "started": "Started",
        "person": "Person",
        "feature": "Feature and record",
        "status": "Outcome",
        "tokens": "Tokens",
        "toolCalls": "Records read",
        "model": "Model and time"
      },
      "status": {
        "RUNNING": "Running",
        "COMPLETED": "Completed",
        "PAUSED": "Waiting for a decision",
        "FAILED": "Failed",
        "CANCELLED": "Cancelled",
        "ABANDONED": "Timed out",
        "EXPIRED": "Expired"
      },
      "reserved": "held",
      "inOut": "{{input}} in · {{output}} out",
      "seconds": "{{n}} s",
      "minutes": "{{m}} min {{s}} s",
      "empty": "No requests this month",
      "emptyBody": "Requests appear here once people use the assistant.",
      "emptyFiltered": "No requests match these filters",
      "clearFilters": "Clear filters",
      "outcome": {
        "AI_DISABLED": "Your Sahab provider had the assistant switched off.",
        "AI_BLOCKED_BY_POLICY": "The assistant wasn’t available for the company then.",
        "AI_KEY_MISSING": "The company’s OpenAI key wasn’t set.",
        "AI_ACK_REQUIRED": "The acknowledgement hadn’t been accepted.",
        "AI_LIMIT_ABOVE_CAP": "The monthly limit was above the cap your Sahab provider set.",
        "AI_MODEL_NOT_ALLOWED": "The model wasn’t allowed for the company.",
        "AI_BUDGET_EXCEEDED": "The token limit had been reached.",
        "AI_RUN_IN_PROGRESS": "Another request of the same person was still running.",
        "AI_RUN_EXPIRED": "Nobody decided on the proposals within 24 hours.",
        "AI_RESUME_INCOMPATIBLE": "The assistant changed while it waited for a decision.",
        "AI_NOT_ALLOWED": "This account couldn’t use the AI assistant.",
        "AI_PROVIDER_AUTH": "OpenAI refused the key.",
        "AI_PROVIDER_UNAVAILABLE": "OpenAI couldn’t be reached.",
        "AI_PROVIDER_RATE_LIMITED": "OpenAI limited the requests on the key.",
        "AI_TURN_LIMIT": "The assistant needed too many steps and stopped.",
        "AI_MODEL_ERROR": "The model returned something unusable.",
        "AI_TIMEOUT": "The request took too long and was stopped."
      }
    },
    "areas": {
      "title": "What the assistant helps with",
      "body": "Turn an area on or off with its switch, then choose its features. A feature also needs its module.",
      "general": "Assistant on every page",
      "changes": "May propose everyday changes",
      "documents": "May read photos and PDFs sent in its panel",
      "documentsBody": "A supplier’s bill or a customer’s order, read into the job it asks for (a draft purchase, invoice or quotation) on a card the person confirms. The files aren’t kept; what is read from them stays with the conversation. Turning it on asks you to accept the documents acknowledgement first.",
      "sensitive": "May also propose sensitive changes",
      "sensitiveBody": "Posting and voiding invoices, purchases and credit notes, recording receipts and supplier payments, and archiving, each on a card the person confirms, within their own permissions and the books’ rules (lock date, approvals). Off until you turn it on.",
      "changesBody": "New and changed customers, suppliers and products, prices, stock adjustments, and drafts of invoices, quotations and purchases, each on a card the person confirms, within their own permissions. Nothing is posted or paid.",
      "projects": "Projects assistant",
      "accounting": "Accounting assistant",
      "inventory": "Inventory assistant",
      "purchasing": "Purchasing assistant",
      "service": "Service assistant",
      "areaSwitch": "Use the {{area}}",
      "notAllowed": "Not allowed by your Sahab provider",
      "notAllowedBody": "Your Sahab provider hasn’t allowed this area for your company. Ask them if you need it.",
      "countOn": "{{on}} of {{all}} on",
      "off": "Off",
      "offBody": "This area is off. Its features keep their ticks, so turning it back on restores them.",
      "documentsHint": "Reading documents stays off until you tick it here.",
      "turnedOn": "On since {{date}}",
      "turnedOnBy": "Turned on by {{name}} on {{date}}",
      "turnedOnByYou": "Turned on by you on {{date}}"
    }
  },
  "privacyRules": {
    "rulesTitle": "Retention rules",
    "runsTitle": "Last retention run"
  },
  "typography": {
    "font": {
      "title": "Typeface",
      "body": "Every face reads Latin and Arabic. IBM Plex is the design system's own.",
      "options": {
        "ibmPlex": "IBM Plex Sans (default)",
        "tajawal": "Tajawal",
        "cairo": "Cairo",
        "almarai": "Almarai",
        "notoSans": "Noto Sans",
        "readexPro": "Readex Pro"
      }
    },
    "textSize": {
      "title": "Text size",
      "body": "Larger text is easier to read; smaller fits more on a screen. Spacing and buttons stay the same.",
      "options": {
        "small": {
          "label": "Small",
          "body": "13px body text: more rows on a screen."
        },
        "default": {
          "label": "Default",
          "body": "14px body text, as designed."
        },
        "large": {
          "label": "Large",
          "body": "15px body text."
        },
        "xlarge": {
          "label": "Extra large",
          "body": "16px body text: easiest to read."
        }
      }
    },
    "preview": {
      "title": "Preview",
      "body": "How a screen reads with this choice, before you save it.",
      "heading": "Invoices",
      "sentence": "Al Noor Trading owes 1,234.500 for October. Send a reminder or record a receipt.",
      "customer": "Al Noor Trading LLC",
      "caption": "Due 13 Nov 2026 · VAT 5%"
    },
    "sampleLatin": "Invoice {{amount}}",
    "sampleArabic": "فاتورة ضريبية · سحاب",
    "save": "Save fonts and text",
    "toast": {
      "saved": "Fonts and text saved. Everyone sees them on their next page load."
    }
  },
  "commissions": {
    "blocks": {
      "approval": "Approval",
      "payment": "Payment"
    },
    "makerChecker": {
      "label": "Whoever calculates a run cannot approve it",
      "hint": "Maker-checker: approval needs a second person."
    },
    "payroll": {
      "label": "Pay approved commission on the payslip",
      "hint": "With Payroll on, an employee linked to the earner is paid on their next payslip; otherwise statements are paid from a cash or bank account."
    },
    "save": "Save commission settings",
    "toast": {
      "saved": "Commission settings saved"
    }
  }
}
